import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ViewportService } from '@core/services/viewport.service';
import { IDeliverableSelectedEvent } from '@cta-analytics-details/cta-analytics-details.interface';
import { EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus, EDeliverableType } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { ICreatorAnalytics, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { EDetailLevel } from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { ECampaignDetailsTab } from '@pages/campaigns/campaign-details/campaign-details.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { SocialContentTileService } from '@shared/components/social-content-tile/social-content-tile.service';
import { find, snakeCase } from 'lodash';
import { CtaAnalyticsDialogContentComponent } from './cta-analytics-dialog-content/cta-analytics-dialog-content.component';
import { CtaAnalyticsDetailsService } from './cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details.service';
import { CtaAnalyticsExecutionService } from './cta-analytics-dialog-content/cta-analytics-table/cta-analytics-execution.service';
import { ICtaAnalyticsData } from './cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { ICtaAnalyticsDialogData, ICtaOption } from './cta-analytics-dialog.interface';
import { CtaAnalyticsDialogStore } from './cta-analytics-dialog.store';
import { ManualFindingStatusStore } from './manual-finding-status.store';

@Component({
  imports: [
    CtaAnalyticsDialogContentComponent,
    IconComponent,
    MatButtonModule,
    NgClass,
    SideDialogComponent,
    TranslateModule,
  ],
  providers: [CtaAnalyticsDialogStore, ManualFindingStatusStore],
  selector: 'app-cta-analytics-dialog',
  templateUrl: './cta-analytics-dialog.component.html',
})
export class CtaAnalyticsDialogComponent {
  public readonly selectedDeliverableId = signal<string | null>(null);
  public readonly selectedDeliverableType = signal<EDeliverableType | null>(null);
  public readonly selectedDeliverableExecutionAnalytics = signal<ICtaAnalyticsData | null>(null);

  public readonly selectedCta = signal<ICtaOption | null>(null);
  public readonly currentCreatorAnalytics = signal<ICreatorAnalytics | null>(null);
  public readonly currentCtaResult = signal<ICtaResult | null>(null);

  public readonly ctaOptions = signal<ICtaOption[]>([]);

  public readonly analyticsNode = computed(() => this.data.analyticsNode);

  public readonly data = inject<ICtaAnalyticsDialogData>(MAT_DIALOG_DATA);
  private readonly viewportService = inject(ViewportService);

  public readonly mobileBriefing = computed(() => this.data.creatorBriefingMode && this.viewportService.isMobile());
  public readonly detailViewOpen = signal<boolean>(false);
  // Bumped by the top-bar "Back to overview" button; the table switches back to its overview panel on each change.
  public readonly backToOverviewRequest = signal<number>(0);

  public readonly ctaAnalyticsDialogStore = inject(CtaAnalyticsDialogStore);
  public readonly manualFindingStatusStore = inject(ManualFindingStatusStore);
  private readonly ctaAnalyticsExecutionService = inject(CtaAnalyticsExecutionService);
  private readonly socialContentTileService = inject(SocialContentTileService);
  private readonly dialogRef = inject(MatDialogRef<CtaAnalyticsDialogComponent>);
  private readonly eventTrackingService = inject(EventTrackingService);

  private deliverableOpenedAt: number | null = null;
  // setupInitialDeliverableSelection()'s effect re-runs whenever currentCtaResult()/analyticsNode()
  // change (e.g. once ctaOptions resolve), but the dialog was only ever "opened into" the
  // deliverable once - this guards against firing campaignAnalyticsDeliverableOpened again on a
  // later re-run of that same effect.
  private initialDeliverableOpenTracked = false;

  constructor() {
    if (this.canFetchFindingStatus()) {
      this.manualFindingStatusStore.loadForCampaign(
        this.data.campaign.id,
        this.data.analyticsNode.content?.id,
        this.data.exportMode ? (this.data.campaignExport?.id ?? null) : null,
      );
    }

    this.setupCtaOptions();
    this.setupInitialDeliverableSelection();

    this.eventTrackingService.track(EEventType.campaignAnalyticsContentOpened, {
      analyticsPopupActive: true,
      campaignId: this.data.campaign.id,
      contentStatus: this.data.analyticsNode.mergedCtaResult?.status,
      currentTab: this.data.currentTab,
      deliverableId: this.data.selectedDeliverableId ?? undefined,
      // Only reported when opened directly on the deliverables tab, per spec - observations also
      // deep-links a deliverable but wasn't called out, so it's deliberately excluded here.
      deliverableType:
        this.data.currentTab === ECampaignDetailsTab.deliverables
          ? (this.data.selectedDeliverableType ?? undefined)
          : undefined,
      expectedKpiId: this.data.expectedKpiId ?? undefined,
      isBriefing: this.data.creatorBriefingMode,
      isReport: this.data.exportMode,
      matchingType: this.data.analyticsNode.content?.matchingType,
      socialContentId: this.data.analyticsNode.content?.id,
      // Opened from a specific kpi-goals row - the kpi type it's for, snake_cased same as the
      // kpi added/removed events' own `source` (see custom-cta-base.directive.ts).
      source:
        this.data.currentTab === ECampaignDetailsTab.kpiGoals && this.data.kpiType
          ? snakeCase(this.data.kpiType)
          : undefined,
      viewMode: this.data.viewMode,
    });

    // #8, manual QA 2026-08-03 - timeSpent for the close event, since only one content popup can
    // be open at a time (this component instance exists only while it's open), a local timestamp
    // captured at open is enough - no cross-component state needed.
    const openedAt = Date.now();

    // Covers every dismissal path (close button, backdrop click, Escape) in one place — a
    // truthy result means onExecutionChange() closed the dialog to reopen with a new tile
    // configuration (a content switch, tracked separately as campaign_analytics_evaluation_context_switched),
    // not a real close, so that case is excluded here.
    this.dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        return;
      }

      // #32/round-2: deliverable_closed must always be reported before content_closed - fire it
      // first (only when a deliverable is actually open; the overview path already fired it).
      if (this.selectedDeliverableId()) {
        this.trackDeliverableClosed();
      }

      this.eventTrackingService.track(EEventType.campaignAnalyticsContentClosed, {
        analyticsPopupActive: false,
        campaignId: this.data.campaign.id,
        deliverableId: this.selectedDeliverableId() ?? undefined,
        isBriefing: this.data.creatorBriefingMode,
        isReport: this.data.exportMode,
        socialContentId: this.data.analyticsNode.content?.id,
        timeSpent: Date.now() - openedAt,
        viewMode: this.data.viewMode,
      });
    });
  }

  // Mirrors DeliverableAnalyticsContentComponent.manualDeliverableStatusConfig().analyticsVisible -
  // manual-finding-status is only ever consulted (to filter out manually-removed findings) where
  // findings are actually shown, so it shouldn't be fetched anywhere else either.
  private canFetchFindingStatus(): boolean {
    if (this.data.creatorBriefingMode) {
      return false;
    }

    if (!this.data.exportMode) {
      return true;
    }

    const { campaignExport } = this.data;

    return !!campaignExport?.showDeliverableStatus && campaignExport?.detailLevel === EDetailLevel.detailedAnalytics;
  }

  public deliverableSelected(event: IDeliverableSelectedEvent): void {
    this.selectedDeliverableId.set(event.deliverableId);
    this.selectedDeliverableType.set(event.deliverableType);
    this.selectedDeliverableExecutionAnalytics.set(event.executionAnalytics);
    this.deliverableOpenedAt = Date.now();

    this.eventTrackingService.track(EEventType.campaignAnalyticsDeliverableOpened, {
      campaignId: this.data.campaign.id,
      contentStatus: this.currentCtaResult()?.status,
      deliverableId: event.deliverableId ?? undefined,
      deliverableType: event.deliverableType,
      isBriefing: this.data.creatorBriefingMode,
      isReport: this.data.exportMode,
      matchingType: event.executionAnalytics?.status,
      socialContentId: this.data.analyticsNode.content?.id,
      viewMode: this.data.viewMode,
    });
  }

  public onOverviewRequested(): void {
    // trackDeliverableClosed() reads the selectedDeliverable* signals - must fire before they're
    // cleared below, or deliverableId/deliverableType go out as undefined on the closed event.
    this.trackDeliverableClosed();

    this.selectedDeliverableId.set(null);
    this.selectedDeliverableType.set(null);
    this.selectedDeliverableExecutionAnalytics.set(null);
    CtaAnalyticsDetailsService.selectedDeliverableUniqueId.set(null);
    CtaAnalyticsDetailsService.previousSelectedDeliverableUniqueId.set(null);
  }

  private trackDeliverableClosed(): void {
    const closedDeliverableId = this.selectedDeliverableId();
    const closedDeliverableType = this.selectedDeliverableType();
    const timeSpent = this.deliverableOpenedAt !== null ? Date.now() - this.deliverableOpenedAt : undefined;
    this.deliverableOpenedAt = null;

    this.eventTrackingService.track(EEventType.campaignAnalyticsDeliverableClosed, {
      campaignId: this.data.campaign.id,
      deliverableId: closedDeliverableId ?? undefined,
      deliverableType: closedDeliverableType ?? undefined,
      isBriefing: this.data.creatorBriefingMode,
      isReport: this.data.exportMode,
      socialContentId: this.data.analyticsNode.content?.id,
      timeSpent,
      viewMode: this.data.viewMode,
    });
  }

  public onBackToOverviewClick(): void {
    this.backToOverviewRequest.update((request) => request + 1);
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  public onExecutionChange(ctaId: string): void {
    if (ctaId === this.selectedCta()?.ctaId) {
      return;
    }

    const ctaOption = find(this.ctaOptions(), { ctaId });

    if (!ctaOption) {
      return;
    }

    this.selectedCta.set(ctaOption);
    this.currentCtaResult.set(this.analyticsNode().content?.ctas?.get(ctaId) as ICtaResult);

    const tileConfiguration = this.socialContentTileService.prepareTileData(
      this.analyticsNode(),
      this.currentCtaResult(),
    );

    this.eventTrackingService.track(EEventType.campaignAnalyticsEvaluationContextSwitched, {
      campaignId: this.data.campaign.id,
      contentStatus: this.currentCtaResult()?.status,
      isBriefing: this.data.creatorBriefingMode,
      isReport: this.data.exportMode,
      matchingType: tileConfiguration.matchingType,
      socialContentId: this.data.analyticsNode.content?.id,
      viewMode: this.data.viewMode,
    });

    this.dialogRef.close(tileConfiguration);
  }

  private setupCtaOptions(): void {
    effect(() => {
      const ctas = Array.from(this.data.analyticsNode.content?.ctas?.values() ?? []);
      const ctaOptions = this.buildCtaOptions(ctas);
      const nextSelectedCta = this.resolveInitialSelectedCta(ctas, ctaOptions);
      const currentSelectedCtaId = this.selectedCta()?.ctaId ?? null;
      const nextSelectedCtaId = nextSelectedCta?.ctaId ?? null;
      const nextCtaResult = nextSelectedCtaId
        ? ((this.analyticsNode().content?.ctas?.get(nextSelectedCtaId) as ICtaResult | undefined) ?? null)
        : null;
      const currentCtaResultId = this.currentCtaResult()?.ctaId ?? null;
      const nextCtaResultId = nextCtaResult?.ctaId ?? null;

      this.ctaOptions.set(ctaOptions);

      if (currentSelectedCtaId !== nextSelectedCtaId) {
        this.selectedCta.set(nextSelectedCta);
      }

      if (currentCtaResultId !== nextCtaResultId) {
        this.currentCtaResult.set(nextCtaResult);
      }
    });
  }

  private buildCtaOptions(ctas: ICtaResultApi[]): ICtaOption[] {
    const ctaOptions: ICtaOption[] = ctas.map((ctaResult) => ({
      ctaId: ctaResult.ctaId,
      name: ctaResult.name,
      platformFeature: this.data.analyticsNode.platformFeature,
    }));

    if (ctaOptions.length > 0) {
      return ctaOptions;
    }

    const nonExecutedCtaOption = this.getNonExecutedCtaOption();

    if (!nonExecutedCtaOption) {
      return [];
    }

    return [nonExecutedCtaOption];
  }

  private getNonExecutedCtaOption(): ICtaOption | null {
    const { nonExecutedCtaId, nonExecutedCtaName } = this.analyticsNode();

    return nonExecutedCtaId && nonExecutedCtaName
      ? {
          ctaId: nonExecutedCtaId,
          name: nonExecutedCtaName,
          platformFeature: this.analyticsNode().platformFeature,
        }
      : null;
  }

  private resolveInitialSelectedCta(ctas: ICtaResultApi[], ctaOptions: ICtaOption[]): ICtaOption | null {
    if (this.data.selectedCtaId) {
      const selectedCta = find(ctaOptions, { ctaId: this.data.selectedCtaId }) ?? null;

      if (selectedCta) {
        return selectedCta;
      }
    }

    const defaultCtaId = this.socialContentTileService.getDefaultSelectedCtaId(ctas);

    if (defaultCtaId) {
      const defaultCta = find(ctaOptions, { ctaId: defaultCtaId }) ?? null;

      if (defaultCta) {
        return defaultCta;
      }
    }

    return ctaOptions[0] ?? null;
  }

  private setupInitialDeliverableSelection(): void {
    effect(() => {
      const selectedDeliverableId = this.data.selectedDeliverableId ?? null;
      const selectedDeliverableType = this.data.selectedDeliverableType ?? null;

      if (!selectedDeliverableId || !selectedDeliverableType) {
        return;
      }

      const executionAnalytics: ICtaAnalyticsData = this.ctaAnalyticsExecutionService.createExecutionData(
        this.analyticsNode(),
        this.currentCtaResult() ?? null,
        this.data.campaignAnalytics.processStatus ?? EProcessStatus.processing,
        this.data.campaign.status ?? ECampaignStatus.draft,
      );

      this.selectedDeliverableId.set(selectedDeliverableId);
      this.selectedDeliverableType.set(selectedDeliverableType);
      this.selectedDeliverableExecutionAnalytics.set(executionAnalytics);

      if (this.initialDeliverableOpenTracked) {
        return;
      }

      this.initialDeliverableOpenTracked = true;
      this.deliverableOpenedAt = Date.now();

      // Opening the dialog straight into a deliverable (e.g. from the deliverables/observations
      // tab) is still a deliverable "open" - report it alongside content_opened, rather than only
      // relying on content_opened's own deliverableId/deliverableType.
      this.eventTrackingService.track(EEventType.campaignAnalyticsDeliverableOpened, {
        campaignId: this.data.campaign.id,
        contentStatus: this.currentCtaResult()?.status,
        deliverableId: selectedDeliverableId,
        deliverableType: selectedDeliverableType,
        isBriefing: this.data.creatorBriefingMode,
        isReport: this.data.exportMode,
        matchingType: executionAnalytics.status,
        socialContentId: this.data.analyticsNode.content?.id,
        viewMode: this.data.viewMode,
      });
    });
  }
}
